import React, { useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableContainer,
  Paper,
  Radio,
  Checkbox,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const RadioSelectPopup = ({
  open,
  onClose,
  data,
  selectedValue,
  setSelectedValue,
  title,
}) => {
  const handleChange = (item) => {
    if (typeof setSelectedValue === "function") {
      setSelectedValue(item); // Update the selected value without closing the popup
    } else {
      console.error("setSelectedValue is not a function");
    }
  };

  const handleOk = () => {
    onClose(); // Close the popup only when OK is pressed
  };

  const handleClose = () => {
    onClose();
  };

  const handleClearAll = () => {
    setSelectedValue(""); // Clear the selected value
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        // Prevent closing on outside click or escape key
        if (reason && reason !== "backdropClick") {
          onClose();
        }
      }}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          marginTop: "7vh",
          backgroundColor: "#000",
          boxShadow: "0px 0px 15px 5px rgba(255, 255, 255, 0.2)",
          borderRadius: "10px",
        },
      }}
    >
      {/* Title */}
      <DialogTitle
        sx={{
          color: "#fff",
          textAlign: "center",
          fontWeight: "bold",
          background: "linear-gradient(45deg, #ffa500, #ffffff)",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          fontSize: "24px",
        }}
      >
        {title}
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 16,
            top: 12,
            color: "#ffa500",
            "&:hover": { color: "#d0892f" },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Content */}
      <DialogContent
        sx={{
          maxHeight: "600px",
          overflowY: "auto",
          padding: "16px",
          backgroundColor: "#000",
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#ffa500",
            borderRadius: "8px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: "#d0892f",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "#333",
          },
        }}
      >
        <TableContainer
          component={Paper}
          sx={{
            backgroundColor: "transparent",
            boxShadow: "none",
          }}
        >
          <Table>
            <TableBody>
              {data.map((item, index) => (
                <TableRow
                  key={index}
                  onClick={() => handleChange(item)} // Clicking the row selects the radio
                  sx={{
                    cursor: "pointer",
                    "&:nth-of-type(odd)": { backgroundColor: "#222" },
                    "&:nth-of-type(even)": { backgroundColor: "#333" },
                  }}
                >
                  {/* Radio Button */}
                  <TableCell
                    sx={{
                      width: "5%",
                      verticalAlign: "middle",
                      borderBottom: "1px solid #444",
                      textAlign: "center",
                    }}
                  >
                    <Radio
                      checked={selectedValue === item}
                      onChange={() => handleChange(item)}
                      sx={{
                        color: "#ffa500",
                        "&.Mui-checked": { color: "#ffa500" },
                      }}
                    />
                  </TableCell>

                  {/* Place of Service Text */}
                  <TableCell
                    sx={{
                      color: "#e9a132",
                      fontWeight: "bold",
                      borderBottom: "1px solid #444",
                      width: "95%",
                      verticalAlign: "middle",
                    }}
                  >
                    {item}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>

      {/* Actions */}
      <DialogActions
        sx={{
          display: "flex",
          justifyContent: "space-between",
          padding: "16px",
          backgroundColor: "#000",
        }}
      >
        <Button
          variant="contained"
          sx={{
            backgroundColor: "#ffa500",
            color: "white",
            "&:hover": { backgroundColor: "#d0892f" },
            padding: "10px 20px",
            fontWeight: "bold",
          }}
          onClick={handleOk} // Close on OK button
        >
          OK
        </Button>
        <Button
          variant="outlined"
          sx={{
            color: "#ffa500",
            borderColor: "#ffa500",
            "&:hover": { borderColor: "#d0892f", color: "#d0892f" },
            padding: "10px 20px",
            fontWeight: "bold",
          }}
          onClick={handleClearAll} // Clear all button
        >
          Clear
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const MultiRadioSelectPopup = ({
  open,
  onClose,
  data,
  selectedValues,
  setSelectedValues,
  title,
}) => {
  const handleToggle = (item) => {
    const currentIndex = selectedValues.indexOf(item);
    const newChecked = [...selectedValues];

    if (currentIndex === -1) {
      newChecked.push(item);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setSelectedValues(newChecked);
  };

  const handleClearAll = () => {
    setSelectedValues([]);
  };

  const handleClose = () => {
    onClose();
  };

  const handleOk = () => {
    onClose(); // Close the popup only when OK is pressed
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        // Prevent closing on outside click or escape key
        if (reason && reason !== "backdropClick") {
          onClose();
        }
      }}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          marginTop: "7vh",
          backgroundColor: "#000",
          boxShadow: "0px 0px 15px 5px rgba(255, 255, 255, 0.2)",
          borderRadius: "10px",
        },
      }}
    >
      {/* Title */}
      <DialogTitle
        sx={{
          color: "#fff",
          textAlign: "center",
          fontWeight: "bold",
          background: "linear-gradient(45deg, #ffa500, #ffffff)",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          fontSize: "24px",
        }}
      >
        {title}
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 16,
            top: 16,
            color: "#ffa500",
            "&:hover": {
              color: "#d0892f",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Content */}
      <DialogContent
        sx={{
          maxHeight: "600px",
          overflowY: "auto",
          padding: "16px",
          backgroundColor: "#000",
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#ffa500",
            borderRadius: "8px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: "#d0892f",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "#333",
          },
        }}
      >
        <TableContainer
          component={Paper}
          sx={{
            backgroundColor: "transparent",
            boxShadow: "none",
          }}
        >
          <Table>
            <TableBody>
              {data.map((item, index) => (
                <TableRow
                  key={index}
                  onClick={() => handleToggle(item)} // Clicking the row selects the checkbox
                  sx={{
                    cursor: "pointer",
                    "&:nth-of-type(odd)": { backgroundColor: "#222" },
                    "&:nth-of-type(even)": { backgroundColor: "#333" },
                  }}
                >
                  {/* Checkbox */}
                  <TableCell
                    sx={{
                      width: "5%",
                      verticalAlign: "middle",
                      borderBottom: "1px solid #444",
                      textAlign: "center",
                    }}
                  >
                    <Checkbox
                      checked={selectedValues.indexOf(item) !== -1}
                      onChange={() => handleToggle(item)}
                      sx={{
                        color: "#ffa500",
                        "&.Mui-checked": { color: "#ffa500" },
                      }}
                    />
                  </TableCell>

                  {/* Place of Service Text */}
                  <TableCell
                    sx={{
                      color: "#e9a132",
                      fontWeight: "bold",
                      borderBottom: "1px solid #444",
                      width: "95%",
                      verticalAlign: "middle",
                    }}
                  >
                    {item}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>

      {/* Actions */}
      <DialogActions
        sx={{
          display: "flex",
          justifyContent: "space-between",
          padding: "16px",
          backgroundColor: "#000",
        }}
      >
        <Button
          variant="contained"
          sx={{
            backgroundColor: "#ffa500",
            color: "white",
            "&:hover": { backgroundColor: "#d0892f" },
            padding: "10px 20px",
            fontWeight: "bold",
          }}
          onClick={handleOk} // Close on OK button
        >
          OK
        </Button>
        <Button
          variant="outlined"
          sx={{
            color: "#ffa500",
            borderColor: "#ffa500",
            "&:hover": { borderColor: "#d0892f", color: "#d0892f" },
            padding: "10px 20px",
            fontWeight: "bold",
          }}
          onClick={handleClearAll} // Clear all button
        >
          Clear All
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export { RadioSelectPopup, MultiRadioSelectPopup };